import { useEffect, useMemo, useState } from 'react';

import { useColorScheme } from '@mui/joy';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { usePermissionsContext } from '../../../context/permissionsContext';
import { track } from '../../../services/analytics';
import {
  EVENT_DASHBOARD_PAGE,
  EVNTAPP_contact_menu_click,
  EVNTAPP_myprofile_menu_click,
  EVNTAPP_programs_menu_click,
  EVNTAPP_retiree_insights_menu_click,
  EVNTAPP_search_menu_click,
  EVNTAPP_signout_menu_click,
  EVNTAPP_research_hub_menu_click,
  EVNT_nav_upgrade_clicked,
  EVNTAPP_following_menu_click,
} from '../../../services/analytics/events';
import { makeAuthenticatedGetRequest } from '../../../services/axios';
import { getFollowedProjectsCount } from '../../../services/axios/endpoints';
import { useGlobalStore } from '../../../stores/useGlobalStore';
import useIsCalyxTeamMember from '../../../utils/hooks/useIsCalyxTeamMember';
import useIsMobile from '../../../utils/hooks/useIsMobile';
import { useLogout } from '../../../utils/hooks/useLogout';
import { usePermissions } from '../../../utils/hooks/usePermissions';
import usePreviousRoute from '../../../utils/hooks/usePreviousRoute';
import { projectDetailRoutes } from '../constants';

export interface NavItem {
  path: string;
  title: string;
  subtitle?: string;
  isNew?: boolean;
  isDropdown?: boolean;
  items?: Array<NavItem>;
  isBeta?: boolean;
  event: string;
  hasPermission?: boolean;
  show: boolean;
  activeCallback?: (path: string) => boolean;
}

export interface ProfileMenuItem {
  title: string;
  show: boolean;
  onClick: () => void;
}

export const FOLLOWING_PROJECTS_ON_SEARCH_PAGE = '/credit-ratings?following=true';

export function useNavbarState() {
  const { hasPermission } = usePermissions();
  const { isFreemiumUser, isVersionMismatch, isDemoUser } = usePermissionsContext();
  const hasPermissionForResearchHub = hasPermission('research_hub:research_list');
  const hasProgramsListPermission = hasPermission('programs:list');
  const hasRetirementInsightsPermission = hasPermission('retirement_insights:list');
  const hasSupplyLinkPermission = hasPermission('dashboard:supply_link');
  const userInfo = useGlobalStore((s) => s.user);
  const logout = useLogout();
  const { mode, setMode } = useColorScheme();
  const navigate = useNavigate();
  const location = useLocation();
  const appStateData = useGlobalStore((s) => ({ isStaging: s.isStaging }));
  const setAppState = useGlobalStore((s) => s.setIsStaging);
  const setMiscState = useGlobalStore((s) => s.setMiscState);
  const isCalyxTeamMember = useIsCalyxTeamMember();
  const isMobile = useIsMobile();
  const prevPath = usePreviousRoute();
  const { filterState } = useGlobalStore((s) => ({
    retireeInsightsRedirectUrl: s.retireeInsightsRedirectUrl,
    filterState: s.filterState,
    prevPath: s.prevPath,
  }));
  const isProjectDeveloper = useMemo(() => userInfo?.company_type === 'developer', [userInfo]);

  const [isHoveringOverInsights, setIsHoveringOverInsights] = useState(false);
  const [isHoveringOverProfile, setIsHoveringOverProfile] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // ---- Nav items config ---------------------------------------------------
  const navItemsConfig: Array<NavItem> = [
    {
      title: 'Credit ratings',
      path: '/credit-ratings',
      isBeta: false,
      activeCallback: (path) => !!projectDetailRoutes.includes(path),
      event: EVNTAPP_search_menu_click,
      show: true,
    },
    {
      title: 'SupplyBridge',
      path: '/supply-bridge',
      isBeta: false,
      isNew: true,
      event: '',
      hasPermission: hasSupplyLinkPermission,
      show: true,
    },
    {
      title: 'Diligence tools',
      path: '/diligence-tools',
      isBeta: false,
      show: true,
      event: '',
      isDropdown: true,
      items: [
        {
          title: 'Calyx Compass',
          subtitle: 'Methodology-specific guidance for early-stage and unrated projects',
          path: '/compass',
          isBeta: false,
          show: true,
          event: '',
        },
        {
          title: 'Program screenings',
          subtitle: 'Assessment results of the programs that issue carbon credits',
          path: '/programs',
          event: EVNTAPP_programs_menu_click,
          hasPermission: hasProgramsListPermission,
          show: true,
        },
        {
          title: 'Expert advisory services',
          subtitle: 'Personalized solutions for every stage of your carbon journey',
          path: '/expert-advisory',
          event: '',
          show: true,
          isNew: true,
        },
      ],
    },
    {
      title: 'Research hub',
      path: '/research-hub',
      event: EVNTAPP_research_hub_menu_click,
      hasPermission: hasPermissionForResearchHub,
      show: true,
    },
    {
      title: 'Retirement insights',
      path: '/retirement_insights',
      event: EVNTAPP_retiree_insights_menu_click,
      hasPermission: hasRetirementInsightsPermission,
      show: true,
    },
    {
      title: 'Developers',
      path: '/developer-portal',
      event: '',
      show: isProjectDeveloper,
    },
  ];

  // ---- Profile menu config ------------------------------------------------
  const profileMenuConfig: ProfileMenuItem[] = [
    {
      title: 'Settings',
      show: true,
      onClick: () => {
        track(EVNTAPP_myprofile_menu_click, EVENT_DASHBOARD_PAGE);
        window.open(
          process.env.IS_PRODUCTION_APP
            ? 'https://auth.calyxglobal.com/account'
            : 'https://auth.calyxstaging.com/account',
          '_blank'
        );
      },
    },
    {
      title: 'Developer Portal',
      show: !isProjectDeveloper && isFreemiumUser,
      onClick: () => {
        navigate('/developer-portal');
      },
    },
    {
      title: 'Contact Us',
      show: !isFreemiumUser,
      onClick: () => {
        track(EVNTAPP_contact_menu_click, EVENT_DASHBOARD_PAGE);
        navigate('/contact', { state: { entryPoint: 'top nav menu' } });
      },
    },
    {
      title: 'Log Out',
      show: true,
      onClick: () => {
        track(EVNTAPP_signout_menu_click, EVENT_DASHBOARD_PAGE);
        logout();
      },
    },
  ];

  const themeOptionsList: Array<{ label: string; value: 'light' | 'dark' }> = [
    { label: 'Light Mode', value: 'light' },
    { label: 'Dark Mode', value: 'dark' },
  ];

  const envOptionsList: Array<{ label: string; value: boolean }> = [
    { label: 'Production', value: false },
    { label: 'Staging', value: true },
  ];

  // ---- Handlers -----------------------------------------------------------
  const handleNavItemClick = (event: string) => {
    track(event, EVENT_DASHBOARD_PAGE);
  };

  const handleThemeSwitch = (m: 'dark' | 'light') => {
    setMode(m);
  };

  const handleEnvToggle = () => {
    setAppState(!appStateData.isStaging);
    window.location.reload();
  };

  const handleUpgradeClick = () => {
    track(EVNT_nav_upgrade_clicked, EVENT_DASHBOARD_PAGE);
    navigate('/upgrade');
  };

  const handleFollowingClick = () => {
    track(EVNTAPP_following_menu_click, EVENT_DASHBOARD_PAGE);
  };

  // ---- Data fetching ------------------------------------------------------
  const { data: followingProjectsCount, isLoading } = useQuery({
    queryKey: [`followed-projects-count`],
    queryFn: async () => await makeAuthenticatedGetRequest(getFollowedProjectsCount),
    select: (data) => data?.data?.count,
  });

  useEffect(() => {
    setMiscState({ prevPath: prevPath || '' });
  }, [prevPath]);

  return {
    // permissions / user
    isFreemiumUser,
    isVersionMismatch,
    isDemoUser,
    userInfo,
    isCalyxTeamMember,
    isMobile,
    isProjectDeveloper,
    // ui state
    mode,
    isHoveringOverInsights,
    setIsHoveringOverInsights,
    isHoveringOverProfile,
    setIsHoveringOverProfile,
    isDrawerOpen,
    setIsDrawerOpen,
    // config
    navItemsConfig,
    profileMenuConfig,
    themeOptionsList,
    envOptionsList,
    appStateData,
    filterState,
    // data
    followingProjectsCount,
    isLoading,
    // navigation
    location,
    navigate,
    logout,
    // handlers
    handleNavItemClick,
    handleThemeSwitch,
    handleEnvToggle,
    handleUpgradeClick,
    handleFollowingClick,
  };
}
